import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'hamstro',
    'captchaFieldset',
    'manualFieldset',
    'notFound',
    'nipInput',
    'errorBox',
    'errorText',
    'showCaptchaFlag',
    'activityBox',
    'ownerNames',
    'searchButton',
  ];

  connect() {
    this.client = null;
    if (this.hasCaptchaFieldsetTarget && !this.captchaFieldsetTarget.classList.contains('hidden')) {
      this.initClient();
    }
  }

  toggle(event) {
    event.preventDefault();
    this.captchaFieldsetTarget.classList.toggle('hidden');
    this.manualFieldsetTarget.classList.toggle('hidden');
    this.notFoundTarget.classList.add('hidden');
    this.clearError();

    const captchaVisible = !this.captchaFieldsetTarget.classList.contains('hidden');
    this.showCaptchaFlagTarget.value = captchaVisible ? 'true' : 'false';
    if (captchaVisible) this.initClient();
  }

  activityChanged(event) {
    this.syncOwnerNamesVisibility(event.target.value);
  }

  search(event) {
    event.preventDefault();
    this.clearError();

    const validationError = this.nipValidationError(this.nipInputTarget.value);
    if (validationError) {
      this.showError(validationError);
      return;
    }

    const client = this.initClient();
    if (!client) {
      this.showError('Wyszukiwarka GUS jest chwilowo niedostępna. Uzupełnij dane ręcznie.');
      return;
    }

    this.searchButtonTarget.setAttribute('disabled', 'disabled');
    const request = client.searchByNip(this.nipInputTarget.value);
    request
      .done((data) => this.handleResponse(data))
      .fail(() => this.showError('Wyszukiwanie nie powiodło się. Spróbuj ponownie lub uzupełnij dane ręcznie.'))
      .always(() => {
        clearTimeout(this.watchdog);
        this.searchButtonTarget.removeAttribute('disabled');
      });

    this.watchdog = setTimeout(() => {
      if (request.state && request.state() === 'pending') {
        this.searchButtonTarget.removeAttribute('disabled');
        this.showError('Wyszukiwanie nie odpowiada. Spróbuj ponownie lub uzupełnij dane ręcznie.');
      }
    }, 15000);
  }

  disconnect() {
    clearTimeout(this.watchdog);
  }

  initClient() {
    if (!this.client && window.hamstro_client && window.jQuery) {
      this.client = window.hamstro_client(window.jQuery(this.hamstroTarget));
    }
    return this.client;
  }

  handleResponse(data) {
    switch (data.status) {
      case 'found':
        this.clearError();
        this.fillForm(data.data);
        this.captchaFieldsetTarget.classList.add('hidden');
        this.manualFieldsetTarget.classList.remove('hidden');
        this.notFoundTarget.classList.add('hidden');
        break;
      case 'remote_not_found':
        this.clearError();
        this.notFoundTarget.classList.remove('hidden');
        break;
      default:
        this.showError(data.message || 'Wyszukiwanie nie powiodło się.');
    }
  }

  fillForm(data) {
    const address = data.address || {};
    const street = address.street_details || {};

    this.setField('form_data_nazwa_firmy', data.name);
    this.setField('form_data_owner_name', data.first_name);
    this.setField('form_data_owner_surname', data.last_name);
    this.setField('form_data_street_name', street.name);
    this.setField('form_data_street_number', street.building_number);
    this.setField('form_data_flat_number', street.door_number);
    this.setField('form_data_kod_pocztowy', address.post_code);
    this.setField('form_data_miejscowosc', address.city);
    this.setField('form_data_province', address.province);
    this.setField('form_data_district', address.district);
    this.setField('form_data_community', address.community);
    this.setField('form_data_nip', data.nip);
    this.setField('form_data_regon', data.regon);

    const activity = data.legal_form && data.legal_form.business_activity;
    if (this.hasActivityBoxTarget && activity) this.setActivity(String(activity));
    this.showCaptchaFlagTarget.value = 'false';
  }

  setActivity(code) {
    const native = this.activityBoxTarget.querySelector('select');
    if (!native) return;

    const option = native.querySelector(`option[value="${code}"]`);
    if (!option) return;

    native.value = code;
    const display = this.activityBoxTarget.querySelector('[data-v2--select-target="display"]');
    if (display) display.textContent = option.textContent;
    this.syncOwnerNamesVisibility(code);
  }

  setField(id, value) {
    const field = this.element.querySelector(`#${id}`);
    if (field) field.value = value || '';
  }

  syncOwnerNamesVisibility(code) {
    if (this.hasOwnerNamesTarget) {
      this.ownerNamesTarget.classList.toggle('hidden', code !== '90099');
    }
  }

  nipValidationError(nip) {
    const digits = (nip || '').replace(/\D/g, '');
    if (digits.length === 0) return 'Proszę podać numer NIP.';
    if (!this.validNip(digits)) return 'Podany NIP jest nieprawidłowy.';
    return null;
  }

  validNip(digits) {
    if (digits.length !== 10) return false;
    const factors = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const sum = factors.reduce((acc, factor, index) => acc + parseInt(digits[index], 10) * factor, 0);
    return sum % 11 === parseInt(digits[9], 10);
  }

  showError(message) {
    this.errorTextTarget.textContent = message;
    this.errorBoxTarget.classList.remove('hidden');
  }

  clearError() {
    this.errorTextTarget.textContent = '';
    this.errorBoxTarget.classList.add('hidden');
  }
}
